import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

/* Mic toggle button: tap to record, tap again to stop -> onAudio(File).
   `busy` = parent is processing the audio (transcribing). */
export function VoiceButton({ onAudio, busy = false, disabled = false, maxSeconds = 120, className = "", size = 16, label = "", testId = "voice-btn" }) {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const stopAll = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecording(false);
    setSecs(0);
  };
  useEffect(() => () => { try { recRef.current?.state === "recording" && recRef.current.stop(); } catch { /* noop */ } stopAll(); }, []); // eslint-disable-line

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        stopAll();
        if (blob.size < 1000) { toast.error("Recording was too short — hold the mic a bit longer"); return; }
        onAudio(new File([blob], `voice.${ext}`, { type }));
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setSecs(0);
      timerRef.current = setInterval(() => setSecs((v) => {
        if (v + 1 >= maxSeconds) { try { rec.stop(); } catch { /* noop */ } return v; }
        return v + 1;
      }), 1000);
    } catch {
      toast.error("Microphone unavailable — check browser permissions");
    }
  };

  const toggle = () => {
    if (busy) return;
    if (recording) { try { recRef.current?.stop(); } catch { stopAll(); } }
    else start();
  };

  return (
    <button onClick={toggle} disabled={disabled || busy} data-testid={testId}
      title={recording ? "Stop recording" : "Record with your voice"}
      className={className || `p-2.5 rounded-xl border ${recording ? "border-rose-500 bg-rose-500/20 text-rose-400 animate-pulse" : "border-white/15 text-zinc-400 hover:text-white hover:border-rose-500"} disabled:opacity-40`}>
      {busy ? <Loader2 size={size} className="animate-spin" />
        : recording ? (
          <span className="flex items-center gap-1.5">
            <Square size={size - 2} fill="currentColor" />
            <span className="text-[11px] font-mono font-bold">{String(Math.floor(secs / 60))}:{String(secs % 60).padStart(2, "0")}</span>
          </span>
        ) : (
          <span className="flex items-center gap-1.5"><Mic size={size} />{label && <span className="text-sm font-bold">{label}</span>}</span>
        )}
    </button>
  );
}
